import * as React from "react";
import { observer } from "mobx-react";
import { action } from "mobx";
import {
    ComboBox,
    type IComboBoxOption,
    type IComboBoxProps,
    type ISelectableOption,
} from "@fluentui/react";
import { TimeFormat } from "../../api";
import { LoadingState } from "../../utils/loading-state";
import { type UUID } from "../../utils/uuid";

export type FormUpdateDefaultTimeFormatProps = Omit<
    IComboBoxProps,
    "options" | "selectedKey" | "onChange"
>;

enum UpdateDefaultTimeFormatFeatures {
    CHANGE_DEFAULT_TIME_FORMAT = "change default time format",
}

import { store } from "../../store";

@observer
export class FormUpdateDefaultTimeFormat extends React.Component<FormUpdateDefaultTimeFormatProps> {
    private readonly loadingFeatures = new LoadingState<UpdateDefaultTimeFormatFeatures>();

    private get options(): ISelectableOption<unknown>[] {
        return Object.values(TimeFormat).map((format) => ({
            key: format,
            text: store.i18n.t(`page.systemWide.defaultTimeFormat.format.${format}`),
        }));
    }

    @action.bound private async updateDefaultTimeFormat(
        publicSettingId: UUID,
        options: IComboBoxOption | undefined,
    ): Promise<void> {
        const defaultTimeFormat = options?.key as TimeFormat;
        await this.loadingFeatures.wrap(
            UpdateDefaultTimeFormatFeatures.CHANGE_DEFAULT_TIME_FORMAT,
            store.repositoryPublicSettings.update(publicSettingId, { defaultTimeFormat }),
        );
    }

    public render(): JSX.Element {
        return (
            <ComboBox
                {...this.props}
                disabled={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultTimeFormatFeatures.CHANGE_DEFAULT_TIME_FORMAT,
                    ) || this.props.disabled
                }
                label={store.i18n.t("page.systemWide.defaultTimeFormat.label")}
                options={this.options}
                selectedKey={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultTimeFormatFeatures.CHANGE_DEFAULT_TIME_FORMAT,
                    )
                        ? undefined
                        : store.servicePublicSettings.publicSetting?.defaultTimeFormat
                }
                onChange={(_, option) => {
                    if (store.servicePublicSettings.publicSetting) {
                        this.updateDefaultTimeFormat(
                            store.servicePublicSettings.publicSetting.id,
                            option,
                        );
                    }
                }}
            />
        );
    }
}
